import { useMemo, useRef, useState } from "react";
import {
  Upload, AlertTriangle, Loader2, CheckCircle2, XCircle,
  RotateCcw, FileArchive, ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

const fmt = (n) => new Intl.NumberFormat("es-DO").format(Math.round(n || 0));
const CONFIRM_TOKEN = "RESTAURAR";

/**
 * Restauración desde ZIP-backup — reverso de BackupDownloadCard.
 *
 * Flujo de seguridad (destructivo):
 *   1. Seleccionar ZIP → click "Analizar backup" (server hace preview,
 *      no toca la DB).
 *   2. Ver metadata + counts. Seleccionar colecciones a restaurar.
 *   3. Escribir "RESTAURAR" en el input de confirmación.
 *   4. Click botón rojo → server dropea la colección y re-inserta.
 *
 * Escenario primario: un partial-REPLACE del uploader de Excel wipeó
 * data histórica en producción; el admin tiene el ZIP del último backup
 * bueno y lo re-inyecta acá.
 */
export default function BackupRestoreCard() {
  const [file, setFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [confirmText, setConfirmText] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const restorable = previewData?.restorable || [];
  const zipCollections = previewData?.collections || {};

  const canRestore =
    !!previewData &&
    selected.size > 0 &&
    confirmText.trim().toUpperCase() === CONFIRM_TOKEN &&
    !restoring;

  const totalSelectedDocs = useMemo(
    () => Array.from(selected).reduce((sum, k) => sum + (zipCollections[k] || 0), 0),
    [selected, zipCollections],
  );

  const reset = () => {
    setFile(null);
    setPreviewData(null);
    setSelected(new Set());
    setConfirmText("");
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreviewData(null);
    setSelected(new Set());
    setConfirmText("");
    setResult(null);
  };

  const handleAnalyze = async () => {
    if (!file) return;
    setAnalyzing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/backup/restore/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreviewData(r.data);
      // Pre-selección: sales por defecto (el caso más común de restore).
      const preSel = new Set();
      if ((r.data.collections || {}).sales) preSel.add("sales");
      setSelected(preSel);
      toast.success("Backup analizado", {
        description: `${fmt(r.data.total_documents)} documentos en total`,
      });
    } catch (e) {
      toast.error("No se pudo analizar el backup",
        { description: formatApiError(e.response?.data?.detail) });
    } finally {
      setAnalyzing(false);
    }
  };

  const toggleCollection = (name) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const handleRestore = async () => {
    if (!canRestore) return;
    setRestoring(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("collections", Array.from(selected).join(","));
      fd.append("confirm", CONFIRM_TOKEN);
      const r = await api.post("/admin/backup/restore", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        // El restore de leads_raw + sales puede tomar 30-60s; subimos el
        // timeout para evitar un aborto prematuro del cliente.
        timeout: 300000,
      });
      setResult(r.data);
      const totalInserted = Object.values(r.data.results || {})
        .reduce((s, v) => s + (v.docs_inserted || 0), 0);
      toast.success("Restore completado", {
        description: `${fmt(totalInserted)} documentos re-insertados en ${
          (r.data.duration_seconds || 0).toFixed(1)}s`,
      });
    } catch (e) {
      toast.error("Error al restaurar",
        { description: formatApiError(e.response?.data?.detail) });
      setResult({ error: formatApiError(e.response?.data?.detail) });
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div className="surface p-6" data-testid="backup-restore-card">
      {/* Header + warning */}
      <div className="flex items-start gap-4 mb-4">
        <div className="kpi-icon-clean" style={{
          width: 44, height: 44, color: "#dc2626",
          boxShadow: "inset 0 0 0 1px #dc262633",
        }}>
          <RotateCcw size={22}/>
        </div>
        <div className="flex-1">
          <h3 className="font-display text-lg font-extrabold" style={{ letterSpacing: "-0.01em" }}>
            Restaurar desde Backup
          </h3>
          <p className="text-xs text-gray-500 mt-1">
            Sube un ZIP generado por &ldquo;Descarga de Backup Completo&rdquo; para revertir
            un wipeout accidental (ej. un REPLACE parcial del uploader).
          </p>
        </div>
      </div>

      <div className="flex items-start gap-2 mb-4 p-3 rounded-lg" style={{
        background: "#fee2e2", border: "1px solid #fecaca",
      }}>
        <ShieldAlert size={16} style={{ color: "#991b1b", flexShrink: 0, marginTop: 2 }}/>
        <div className="text-[11px] font-semibold" style={{ color: "#7f1d1d", lineHeight: 1.5 }}>
          <strong>OPERACIÓN DESTRUCTIVA</strong> — cada colección seleccionada se
          <em> dropea completamente</em> antes de re-insertar el contenido del ZIP.
          La data actual que no esté en el backup se pierde. Descarga primero un
          backup fresco del estado actual si tienes dudas.
        </div>
      </div>

      {/* Step 1: file picker */}
      <div className="mb-4">
        <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-2">
          1. Archivo ZIP de backup
        </label>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            ref={fileInputRef}
            type="file"
            accept=".zip,application/zip"
            onChange={handleFileChange}
            data-testid="restore-file-input"
            className="text-xs"
            style={{
              flex: 1, minWidth: 220, padding: "8px 10px",
              border: "1px solid #e2e8f0", borderRadius: 8, background: "#f8fafc",
            }}
          />
          <button
            type="button"
            onClick={handleAnalyze}
            disabled={!file || analyzing}
            data-testid="restore-analyze-btn"
            className="btn"
            style={{
              background: !file || analyzing ? "#e5e7eb" : "#0f172a",
              color: !file || analyzing ? "#9ca3af" : "#ffffff",
              fontWeight: 800, padding: "8px 14px", borderRadius: 8,
              border: "none", cursor: !file || analyzing ? "not-allowed" : "pointer",
              display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12,
            }}
          >
            {analyzing
              ? <><Loader2 size={14} className="animate-spin"/> Analizando…</>
              : <><FileArchive size={14}/> Analizar backup</>}
          </button>
          {previewData && (
            <button
              type="button"
              onClick={reset}
              data-testid="restore-reset-btn"
              className="text-xs font-bold text-gray-500 hover:text-gray-900"
              style={{ padding: "8px 10px" }}
            >
              Descartar
            </button>
          )}
        </div>
        {file && (
          <div className="text-[11px] text-gray-500 mt-1">
            {file.name} · {fmt(file.size / 1024)} KB
          </div>
        )}
      </div>

      {/* Step 2: preview + collection selector */}
      {previewData && (
        <>
          {previewData.metadata && (
            <div className="mb-4 p-3 rounded-lg" style={{
              background: "#f0f9ff", border: "1px solid #bae6fd",
            }}>
              <div className="text-[11px] font-bold text-sky-900 uppercase tracking-wider mb-1">
                Metadata del backup
              </div>
              <div className="text-xs text-sky-950 space-y-0.5">
                <div><strong>Creado:</strong> {new Date(previewData.metadata.created_at).toLocaleString("es-DO")}</div>
                <div><strong>Por:</strong> {previewData.metadata.created_by || "—"}</div>
                <div><strong>Total docs:</strong> {fmt(previewData.metadata.total_documents)}</div>
              </div>
            </div>
          )}

          <div className="mb-4">
            <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-2">
              2. Colecciones a restaurar ({selected.size} seleccionadas · {fmt(totalSelectedDocs)} docs)
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="restore-collection-list">
              {Object.entries(zipCollections).map(([name, count]) => {
                const canPick = restorable.includes(name);
                const isSel = selected.has(name);
                return (
                  <label
                    key={name}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg"
                    data-testid={`restore-coll-${name}`}
                    style={{
                      background: !canPick ? "#f3f4f6" : isSel ? "#fef3c7" : "#ffffff",
                      border: `1px solid ${!canPick ? "#e5e7eb" : isSel ? "#fbbf24" : "#e2e8f0"}`,
                      cursor: canPick ? "pointer" : "not-allowed",
                      opacity: canPick ? 1 : 0.5,
                    }}
                  >
                    <input
                      type="checkbox"
                      disabled={!canPick}
                      checked={isSel}
                      onChange={() => toggleCollection(name)}
                    />
                    <span className="text-[11px] font-bold flex-1" style={{
                      fontFamily: "monospace", letterSpacing: "-0.02em",
                    }}>{name}</span>
                    <span className="text-xs font-extrabold text-gray-900 tabular-nums">
                      {fmt(count)}
                    </span>
                    {!canPick && (
                      <span className="text-[9px] font-bold text-gray-500 uppercase">
                        no permitido
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
            {previewData.unsupported?.length > 0 && (
              <div className="text-[10px] text-gray-500 mt-2">
                <strong>users</strong> queda excluido por seguridad — reactivar cuentas
                revocadas o pisar contraseñas requiere acción manual.
              </div>
            )}
          </div>

          {/* Step 3: confirmation token */}
          <div className="mb-4">
            <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-2">
              3. Escribe <span style={{ color: "#dc2626" }}>{CONFIRM_TOKEN}</span> para confirmar
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={CONFIRM_TOKEN}
              data-testid="restore-confirm-input"
              autoComplete="off"
              className="text-sm w-full"
              style={{
                padding: "10px 12px", border: "1px solid #e2e8f0",
                borderRadius: 8, fontWeight: 800, letterSpacing: "0.1em",
                background: "#ffffff",
              }}
            />
          </div>

          {/* Step 4: destructive button */}
          <button
            type="button"
            onClick={handleRestore}
            disabled={!canRestore}
            data-testid="restore-execute-btn"
            className="btn w-full"
            style={{
              background: !canRestore ? "#e5e7eb" : "#dc2626",
              color: !canRestore ? "#9ca3af" : "#ffffff",
              fontWeight: 800, padding: "12px 18px", borderRadius: 10,
              border: "none", cursor: !canRestore ? "not-allowed" : "pointer",
              display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
              fontSize: 13,
              boxShadow: !canRestore ? "none" : "0 4px 12px rgba(220, 38, 38, 0.35)",
            }}
          >
            {restoring
              ? <><Loader2 size={16} className="animate-spin"/> Restaurando… (puede tardar 30-60s)</>
              : <><Upload size={16}/> Ejecutar restore ({selected.size} colecciones · {fmt(totalSelectedDocs)} docs)</>}
          </button>
        </>
      )}

      {/* Results */}
      {result && !result.error && (
        <div className="mt-5 p-4 rounded-lg" style={{
          background: "#f0fdf4", border: "1px solid #bbf7d0",
        }} data-testid="restore-result-panel">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 size={18} style={{ color: "#16a34a" }}/>
            <div className="font-extrabold text-emerald-900">
              Restore completado en {(result.duration_seconds || 0).toFixed(1)}s
            </div>
          </div>
          <div className="text-xs space-y-2">
            {Object.entries(result.results || {}).map(([coll, r]) => (
              <div key={coll} className="flex items-center justify-between px-3 py-2 rounded"
                style={{ background: "#ffffff", border: "1px solid #d1fae5" }}>
                <span className="font-bold" style={{ fontFamily: "monospace" }}>{coll}</span>
                <span className="text-emerald-800 font-extrabold tabular-nums">
                  {fmt(r.docs_before)} → {fmt(r.docs_after)}
                  <span className="text-emerald-500 font-normal ml-2">
                    ({fmt(r.docs_inserted)} insertados)
                  </span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {result?.error && (
        <div className="mt-5 p-4 rounded-lg" style={{
          background: "#fef2f2", border: "1px solid #fecaca",
        }}>
          <div className="flex items-center gap-2">
            <XCircle size={18} style={{ color: "#dc2626" }}/>
            <div className="font-extrabold text-red-900">Falló el restore</div>
          </div>
          <div className="text-xs text-red-800 mt-2">{result.error}</div>
        </div>
      )}

      {!previewData && !file && (
        <div className="flex items-start gap-2 mt-2 p-3 rounded-lg" style={{
          background: "#f8fafc", border: "1px dashed #cbd5e1",
        }}>
          <AlertTriangle size={14} style={{ color: "#64748b", flexShrink: 0, marginTop: 2 }}/>
          <div className="text-[11px] font-semibold text-slate-600" style={{ lineHeight: 1.5 }}>
            Tip: primero baja el backup actual desde el card de arriba, luego
            sube <em>ese</em> ZIP acá si necesitas revertir. La colección{" "}
            <strong>users</strong> nunca se restaura para no pisar credenciales.
          </div>
        </div>
      )}
    </div>
  );
}
